// Numbers that stay unique offline (plan §9.4). One counter per device, in the local
// database, incremented inside the same transaction as the record it numbers — so a crash
// either keeps both or neither. The same counter gives each command its `device_seq` and
// each document its human number (`PB-K7Q-00481`): they never repeat on a device.
//
// A station that lost its database resumes above the server's `device.last_device_seq`
// (synced down, stream `station_devices`), and a re-paired install gets a new device code,
// so numbers never collide even then.

import { type DocPrefix, formatDocNumber, isValidDeviceCode } from '@enp/domain';
import type { LocalTx } from './engine.js';

/** Records this device's code (at pairing). Keeps an existing counter. */
export async function initDeviceSequence(
  tx: LocalTx,
  deviceId: string,
  deviceCode: string,
): Promise<void> {
  if (!isValidDeviceCode(deviceCode)) throw new RangeError(`Invalid device code: ${deviceCode}`);
  const row = await tx.getOptional<{ id: string }>('SELECT id FROM device_sequence WHERE id = ?', [
    deviceId,
  ]);
  if (row) {
    await tx.execute('UPDATE device_sequence SET device_code = ? WHERE id = ?', [
      deviceCode,
      deviceId,
    ]);
  } else {
    await tx.execute('INSERT INTO device_sequence (id, device_code, last_seq) VALUES (?, ?, 0)', [
      deviceId,
      deviceCode,
    ]);
  }
}

/** The next number on this device, and its device code. Call inside the record's transaction. */
export async function nextDeviceSeq(
  tx: LocalTx,
  deviceId: string,
): Promise<{ seq: number; deviceCode: string | null }> {
  const local = await tx.getOptional<{ device_code: string | null; last_seq: number | null }>(
    'SELECT device_code, last_seq FROM device_sequence WHERE id = ?',
    [deviceId],
  );
  const synced = await tx.getOptional<{
    device_code: string | null;
    last_device_seq: number | null;
  }>('SELECT device_code, last_device_seq FROM device WHERE id = ?', [deviceId]);
  const seq = Math.max(Number(local?.last_seq ?? 0), Number(synced?.last_device_seq ?? 0)) + 1;
  if (!Number.isSafeInteger(seq)) throw new RangeError('Device sequence overflow');
  const deviceCode = local?.device_code ?? synced?.device_code ?? null;
  if (local) {
    await tx.execute('UPDATE device_sequence SET last_seq = ? WHERE id = ?', [seq, deviceId]);
  } else {
    await tx.execute('INSERT INTO device_sequence (id, device_code, last_seq) VALUES (?, ?, ?)', [
      deviceId,
      deviceCode,
      seq,
    ]);
  }
  return { seq, deviceCode };
}

/**
 * The next human document number on this device, e.g. `PB-K7Q-00481`. Call inside the
 * transaction that creates the record (and enqueues its command).
 */
export async function nextDocNumber(
  tx: LocalTx,
  deviceId: string,
  prefix: DocPrefix,
): Promise<string> {
  const { seq, deviceCode } = await nextDeviceSeq(tx, deviceId);
  if (!deviceCode) throw new Error('This device has no device code yet (pair it first)');
  return formatDocNumber(prefix, deviceCode, seq);
}
